import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import arrayMove from 'array-move';
import DateTimeInput from './DateTimeInput';
import MarkdownInput from './MarkdownInput';
import Modal from './Modal';

import {
  SortableContainer,
  SortableElement,
  SortableHandle
} from 'react-sortable-hoc';

const DragHandle = SortableHandle(() =>
  <div className="u-flex u-items-center u-rounded-l u-bg-grey-dark u-text-white u-px-3 u-cursor-move">
    <i className="Icon Icon-hamburger u-text-xs"></i>
  </div>
);

const SortableItem = SortableElement(props => {
    const refsInputs = {
      name: React.createRef(),
      due_date: React.createRef(),
      notes: React.createRef()
    };
    const task = {
      id: props.task.id,
      name: props.task.name,
      notes: props.task.notes,
      position: props.task.position,
      due_date: props.task.due_date,
      guid: props.task.guid,
      has_completions: props.task.has_completions,
      confirmed_update: props.task.confirmed_update !== undefined ? props.task.confirmed_update : false
    };
    function handleTaskUpdate(attribute, value) {
      if ((!task.has_completions || task.confirmed_update) || (task.has_completions && !task.confirmed_update &&
          confirm("This checklist is already visible and may have been completed by recipients, so editing is not advised."))) {
        task.confirmed_update = true;
        task[attribute] = value;
        props.onTaskUpdate(task);
      } else {
        if (attribute === 'notes') {
          refsInputs[attribute].current.value = task[attribute];
        } else {
          refsInputs[attribute].value = task[attribute];
        }
      }
    }
    return (
      <div className="u-flex u-rounded u-border u-border-grey-light u-mb-2">
        <DragHandle />
        <div className="u-flex u-items-start u-flex-grow u-justify-between">
          <div className="u-flex u-flex-col u-flex-grow">
            <input type="text" placeholder="Add Task" defaultValue={props.task.name} onBlur={(e) => handleTaskUpdate('name', e.target.value)} className="u-font-semibold u-mb-0 u-border-none" ref={ el => refsInputs.name = el } />
            <MarkdownInput
              toolbarAlwaysInvisible={true}
              rows={1}
              style={{ minHeight: 24 }}
              placeholder="Add Notes (optional)"
              value={props.task.notes}
              onCustomBlur={(value) => handleTaskUpdate('notes', value)}
              wrapperClassName="u-mb-0 u-w-full u-border-none"
              className="u-p-2"
              innerRef={refsInputs.notes}
            />
          </div>
          <div className="u-flex u-items-center u-justify-end">
            <span className="u-text-grey u-italic u-text-sm u-mr-1">
              { props.task.due_date && moment(props.task.due_date).format('MMMM D, YYYY') }
              { !props.task.due_date && "(no deadline)" }
            </span>
            <span className="u-mr-1">
              <DateTimeInput innerRef={el => refsInputs.due_date = el } time={false} onChange={(value) => handleTaskUpdate('due_date', value)} onlyIcon={true} alignRight={true} />
            </span>
            <i className="Icon-delete u-mr-3 u-text-sm u-text-old-primary-color-red u-cursor-pointer" onClick={props.onTaskDeleteClick} />
          </div>
        </div>
      </div>
    );
  }
);

const SortableList = SortableContainer(props => {
  return (
    <div>
      { props.tasks.map((task, index) => (
        <SortableItem
          key={`task-${index}-${task.id !== undefined ? task.id : task.guid}`}
          index={index}
          sortable={true}
          onTaskUpdate={(task) => props.onTaskUpdate(index, task)}
          onTaskDeleteClick={() => props.onTaskDeleteClick(index)}
          task={task}
        />
      ))}
    </div>
  );
});

export default class SortableTasksInput extends Component {
  static defaultProps = {
    tasks: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      position: PropTypes.number.isRequired,
      has_completions: PropTypes.bool.isRequired,
      due_date: PropTypes.string,
      guid: PropTypes.string,
      notes: PropTypes.string,
      confirmed_update: PropTypes.bool
    })),
  }

  constructor(props) {
    super(props);
    this.state = {
      tasks: this.props.tasks.concat([{ position: this.props.tasks.length + 1, guid: this.uuidv4() }]),
      taskToDelete: null,
    };
  }

  uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  handleNewTaskClick = (e) => {
    e.preventDefault();

    const tasks = this.state.tasks;
    tasks.push({ position: this.state.tasks.length + 1, guid: this.uuidv4() });
    this.setState({ tasks });
  }

  handleTaskUpdate = (index, value) => {
    const tasks = this.state.tasks;
    tasks[index] = value;
    this.setState({ tasks });
  }

  handleTaskDeleteClick = (index) => {
    this.setState({ taskToDelete: index });
  }

  handleDismissModal = () => {
    this.setState({ taskToDelete: null });
  }

  handleTaskDelete = () => {
    const tasks = this.state.tasks;
    const index = this.state.taskToDelete;
    for (let i = index; i < this.state.tasks.length; i++) {
      tasks[i].position--;
    }
    tasks.splice(index, 1);
    this.setState({ tasks, taskToDelete: null })
  }

  handleSortEnd = ({oldIndex, newIndex}) => {
    let tasks = arrayMove(this.state.tasks, oldIndex, newIndex);
    tasks = tasks.map((task, index) => {
      const taskUpdate = task;
      taskUpdate.position = index + 1;
      return taskUpdate;
    });
    this.setState({ tasks });
  }

  render () {
    const tasksValue = JSON.stringify(this.state.tasks.reduce((tasks, task) => {
      if (task.name && task.name !== '') {
       tasks.push(Object.assign({}, task, {
         guid: undefined,
         has_completions: undefined,
         confirmed_update: undefined
       }))
      }
      return tasks;
    }, []))
    return (
      <div className="u-flex u-flex-col">
        <SortableList tasks={this.state.tasks} onTaskUpdate={this.handleTaskUpdate} useDragHandle onSortEnd={this.handleSortEnd} onTaskDelete={this.handleTaskDelete} onTaskDeleteClick={this.handleTaskDeleteClick} />
        <div className="u-flex u-justify-end">
          <button className="Button Button--small" onClick={this.handleNewTaskClick}>
            <i className="Icon-plus-square u-text-old-primary-color-blue u-mr-2"></i>
            Add task
          </button>
        </div>
        <input type="hidden" name="checklist[tasks]" value={tasksValue}/>
        { this.state.taskToDelete !== null && (
          <Modal onClose={() => this.setState({ taskToDelete: null })} modalWrapperClassName="u-max-w-md">
            <div className="u-p-6 u-pt-4 u-relative">
              <span className="Modal-close Modal-dismiss" onClick={this.handleDismissModal}>
                <i className="Icon Icon-close"></i>
              </span>
              <h4 className="u-text-2xl u-font-semibold u-leading-none u-mt-0 u-mb-4">Delete task?</h4>
              <div>The task and any completion data associated with it will be lost. Do you want to continue?</div>
              <div className="u-mt-4 u-flex u-w-full u-justify-end">
                <span className="Button Button--primary Button--red u-cursor-pointer u-mr-2" onClick={this.handleTaskDelete}>Yes, delete task</span>
                <span className="Button Button--grey Modal-dismiss u-cursor-pointer" onClick={this.handleDismissModal}>No, go back</span>
              </div>
            </div>
          </Modal>)
        }
      </div>
    );
  }
}
